import { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { Shield, Lock, Eye, Trash2, Mail, ChevronLeft } from "lucide-react";

const SECTIONS = [
  {
    id: "collect",
    icon: Eye,
    color: "#3B82F6",
    title: "البيانات التي نجمعها",
    content: [
      "**بيانات الحساب**: الاسم، البريد الإلكتروني، كلمة المرور المشفّرة عند التسجيل.",
      "**بيانات التكاملات**: مفاتيح API لقيود ورموز OAuth لسلة — تُخزَّن مشفّرة ولا تُشارك مع أطراف ثالثة.",
      "**بيانات المخزون**: معلومات المنتجات والكميات التي يتم مزامنتها بين قيود وسلة.",
      "**سجلات النشاط**: تواريخ وأوقات عمليات المزامنة والأخطاء لأغراض التشخيص.",
      "**بيانات الاستخدام**: معلومات تقنية كعنوان IP ونوع المتصفح لتحسين الخدمة.",
    ],
  },
  {
    id: "use",
    icon: Shield,
    color: "#10B981",
    title: "كيف نستخدم بياناتك",
    content: [
      "تشغيل خدمة المزامنة التلقائية بين قيود وسلة.",
      "إرسال إشعارات بريدية ضرورية (كتأكيد الحساب وإشعارات الأخطاء).",
      "تحسين أداء الخدمة وتشخيص المشكلات التقنية.",
      "الامتثال للمتطلبات القانونية والتنظيمية في المملكة العربية السعودية.",
      "**لن** نستخدم بياناتك للإعلانات ولن نبيعها لأي طرف ثالث.",
    ],
  },
  {
    id: "share",
    icon: Lock,
    color: "#A78BFA",
    title: "مشاركة البيانات",
    content: [
      "**قيود**: نرسل ونستقبل بيانات المخزون فقط — بناءً على إذنك الصريح.",
      "**سلة**: نرسل تحديثات المخزون إلى متجرك — بناءً على إذنك الصريح.",
      "**مزودو الخدمة**: نستخدم خدمات سحابية موثوقة (مثل AWS/MongoDB Atlas) لتشغيل البنية التحتية، وجميعها ملتزمة بأعلى معايير الأمن.",
      "لا نشارك بياناتك مع أي جهة تسويقية أو إعلانية.",
    ],
  },
  {
    id: "security",
    icon: Lock,
    color: "#F59E0B",
    title: "أمن البيانات",
    content: [
      "جميع البيانات مشفّرة أثناء النقل باستخدام TLS 1.3.",
      "كلمات المرور مشفّرة بخوارزمية bcrypt — لا يمكن لأحد من فريقنا الاطلاع عليها.",
      "مفاتيح API ورموز OAuth مشفّرة في قاعدة البيانات.",
      "نجري مراجعات أمنية دورية لضمان حماية بياناتك.",
      "بيانات كل مستأجر معزولة تماماً عن الآخرين (Multi-tenant isolation).",
    ],
  },
  {
    id: "rights",
    icon: Shield,
    color: "#EF4444",
    title: "حقوقك",
    content: [
      "**الوصول**: يمكنك طلب نسخة من جميع بياناتك المخزّنة.",
      "**التصحيح**: يمكنك تحديث بياناتك الشخصية من لوحة الإعدادات.",
      "**الحذف**: يمكنك طلب حذف حسابك وجميع بياناتك المرتبطة به.",
      "**الاعتراض**: يمكنك إيقاف معالجة بياناتك في أي وقت.",
      "لممارسة أي من هذه الحقوق، تواصل معنا عبر البريد الإلكتروني أدناه.",
    ],
  },
];

export default function PrivacyPage() {
  const { name: platformName, logo_base64 } = usePlatform();

  useEffect(() => {
    document.title = `سياسة الخصوصية — ${platformName || "جسر"}`;
    window.scrollTo(0, 0);
  }, [platformName]);

  return (
    <div dir="rtl" className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Tajawal', sans-serif" }}>

      {/* Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logo_base64
              ? <img src={logo_base64} alt={platformName} className="w-7 h-7 rounded-lg object-contain"/>
              : <JasrMark className="w-7 h-7"/>}
            <span className="font-bold text-slate-900">{platformName || "جسر"}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004EEB] transition-colors">
            <ChevronLeft className="w-4 h-4"/>
            العودة للرئيسية
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-[#030B1A] py-14 sm:py-20 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(0,78,235,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,78,235,0.04) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-bold"
            style={{ background: 'rgba(0,78,235,0.15)', border: '1px solid rgba(0,78,235,0.3)', color: '#93C5FD' }}>
            <Shield className="w-3.5 h-3.5"/> قانوني
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4">سياسة الخصوصية</h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
            نحن نقدّر خصوصيتك ونلتزم بحماية بياناتك الشخصية وبيانات متجرك.
          </p>
          <p className="text-slate-500 text-sm mt-4">آخر تحديث: فبراير 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">

        {/* Intro */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 mb-8 shadow-sm">
          <p className="text-slate-600 leading-relaxed text-base">
            تصف هذه السياسة كيفية جمع منصة <strong className="text-slate-900">{platformName || "جسر"}</strong> ("نحن"، "المنصة") لبياناتك الشخصية واستخدامها وحمايتها عند استخدامك لخدماتنا.
            باستخدامك للمنصة، فأنت توافق على ممارسات الخصوصية المبيّنة في هذه الوثيقة.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {SECTIONS.map((sec) => (
            <div key={sec.id} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm hover:border-[#004EEB]/20 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${sec.color}15` }}>
                  <sec.icon className="w-5 h-5" style={{ color: sec.color }} strokeWidth={1.5}/>
                </div>
                <h2 className="text-xl font-bold text-slate-900">{sec.title}</h2>
              </div>
              <ul className="space-y-3">
                {sec.content.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-2"/>
                    <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-800">$1</strong>') }}/>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Cookies */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900 mb-4">ملفات الارتباط (Cookies)</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              نستخدم ملفات الارتباط الضرورية فقط لتشغيل جلسة تسجيل الدخول وحفظ تفضيلات المظهر (فاتح/داكن). لا نستخدم ملفات الارتباط للإعلانات أو التتبع.
            </p>
          </div>

          {/* Contact */}
          <div className="rounded-2xl p-7" style={{ background: 'linear-gradient(135deg, #004EEB 0%, #0038CC 100%)' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-white" strokeWidth={1.5}/>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">تواصل معنا</h2>
                <p className="text-blue-100 text-sm leading-relaxed mb-4">
                  لأي استفسارات حول هذه السياسة، أو لممارسة حقوقك المتعلقة بالبيانات، تواصل مع فريقنا:
                </p>
                <a href="mailto:support@getjisr.com"
                  className="inline-flex items-center gap-2 bg-white text-[#004EEB] px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-50 transition-colors">
                  <Mail className="w-4 h-4"/>
                  support@getjisr.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center mt-12 pt-8 border-t border-slate-200">
          <p className="text-slate-400 text-sm">
            نحتفظ بحق تعديل هذه السياسة في أي وقت. سنُعلمك بأي تغييرات جوهرية عبر البريد الإلكتروني.
          </p>
          <div className="flex items-center justify-center gap-4 mt-5">
            <Link to="/" className="text-sm text-[#004EEB] hover:underline">الصفحة الرئيسية</Link>
            <span className="text-slate-300">·</span>
            <Link to="/terms" className="text-sm text-[#004EEB] hover:underline">شروط الاستخدام</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
